import { useState } from 'react';
import { BoutonDocument } from '../../components/BoutonDocument.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { ouvreVisionneuse } from '../../components/Visionneuse.jsx';
import { congesApi, dossierApi, employesApi } from '../../api/resources';
import { nomComplet } from '../../lib/acces';
import { dateSuisse } from '../../lib/dates';
import { tr, trf } from '../../lib/i18n';
import { brutDuMois, moisDe, moisDesFiches } from '../../lib/salaires-periode';
import { hours } from '../../lib/utils';
import { C } from '../../theme/palette';

const MOIS = [
  'Janvier',
  'Février',
  'Mars',
  'Avril',
  'Mai',
  'Juin',
  'Juillet',
  'Août',
  'Septembre',
  'Octobre',
  'Novembre',
  'Décembre',
];

const ABSENCES = {
  vacances: '🌴 Vacances',
  maladie: '🤒 Maladie',
  accident: '🩹 Accident',
  formation: '🎓 Formation',
};

/** « 2026-08-03 » s'écrit « 03.08.2026 » ; une date déjà suisse reste telle quelle. */
const jourSuisse = (d) => (/^\d{4}-\d{2}-\d{2}/.test(String(d || '')) ? dateSuisse(d) : d || '');

/** Clé de tri d'une date suisse ou ISO : « AAAA-MM-JJ ». */
function jourTri(d) {
  const s = String(d || '');
  const m = s.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})/);
  return m ? m[3] + '-' + m[2].padStart(2, '0') + '-' + m[1].padStart(2, '0') : s;
}

const ligne = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: 6,
  fontSize: 10.5,
  padding: '6px 0',
  borderTop: '1px solid #f0f2f5',
};
const lien = { fontSize: 10.5, fontWeight: 800, color: C.purple, textDecoration: 'none' };
const vide = { fontSize: 9.5, color: C.muted, padding: '2px 0 4px' };
const cellule = { padding: '6px 4px', fontSize: 10, textAlign: 'left', verticalAlign: 'top' };
const entete = {
  ...cellule,
  fontSize: 8.5,
  fontWeight: 800,
  color: C.muted,
  textTransform: 'uppercase',
};

/** Un fichier du casier : il s'ouvre dans la visionneuse, avec « ← Retour ». */
function Voir({ adresse, titre, libelle }) {
  return (
    <a
      href={adresse}
      target="_blank"
      rel="noreferrer"
      style={lien}
      onClick={(ev) => {
        if (ouvreVisionneuse({ adresse, titre })) ev.preventDefault();
      }}
    >
      {libelle}
    </a>
  );
}

/** Un petit dossier du casier : une ligne, qu'on touche pour l'ouvrir. */
function Tiroir({ icone, libelle, resume, children }) {
  return (
    <details data-testid="tiroir" style={{ borderTop: '1px solid ' + C.border }}>
      <summary
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '11px 0',
          cursor: 'pointer',
          listStyle: 'none',
        }}
      >
        <span style={{ fontSize: 15, width: 22, textAlign: 'center' }}>{icone}</span>
        <span style={{ flex: 1, minWidth: 0, fontSize: 11.5, fontWeight: 800, color: C.ink }}>
          {libelle}
        </span>
        <span style={{ fontSize: 10, fontWeight: 800, color: resume ? C.purple : C.muted }}>
          {resume || '—'}
        </span>
        <span style={{ fontSize: 10, color: C.muted }}>▸</span>
      </summary>
      <div style={{ padding: '0 0 10px 30px' }}>{children}</div>
    </details>
  );
}

/** Du … au … : un seul jour s'écrit une fois. */
const periode = (c) =>
  c.debut === c.fin || !c.fin
    ? jourSuisse(c.debut)
    : jourSuisse(c.debut) + ' → ' + jourSuisse(c.fin);

const totalJours = (liste) => liste.reduce((t, c) => t + (+c.jours || 0), 0);

/** Le casier d'une personne : tout ce qui est à elle, rangé en petits dossiers. */
function Casier({ e, annee, absences, estPatron, chantiers }) {
  const [lettres, setLettres] = useState(null);
  const nom = nomComplet(e) || e.nom || tr('Employé');
  // Un mois sans salaire (payé à l'heure, sans heure) n'a pas de fiche : le
  // casier alignait des fiches à CHF 0.00 de janvier à septembre.
  const fiches = moisDesFiches(e, annee).filter((m) => brutDuMois(e, chantiers, m) > 0);
  const vacances = absences.filter((c) => (c.type || 'vacances') === 'vacances');
  // Ses heures de l'année, mois par mois et chantier par chantier : les
  // pointages des chantiers, plus ceux de l'atelier ou du dépôt.
  const heuresParMois = {};
  const ajoute = (date, chantier, h) => {
    const m = moisDe(date);
    if (!m.startsWith(String(annee)) || !(h > 0)) return;
    const mois = (heuresParMois[m] = heuresParMois[m] || { total: 0, chantiers: {} });
    mois.total += h;
    mois.chantiers[chantier] = (mois.chantiers[chantier] || 0) + h;
  };
  (chantiers || []).forEach((c) =>
    (c.pointages || [])
      .filter((p) => p.empId === e.id && p.type !== 'absence')
      .forEach((p) => ajoute(p.date, c.nom || tr('Chantier'), hours(p.entree, p.sortie, p.pause))),
  );
  (e.badges || [])
    .filter((b) => b.chId == null && b.type !== 'absence')
    .forEach((b) => ajoute(b.date, tr('Atelier / dépôt'), hours(b.entree, b.sortie, b.pause)));
  const moisHeures = Object.keys(heuresParMois).sort().reverse();
  const totalHeures = moisHeures.reduce((t, m) => t + heuresParMois[m].total, 0);
  // Ce qu'il a fait sur les chantiers : ses rapports du jour, le plus récent d'abord.
  const travaux = (chantiers || [])
    .flatMap((c) =>
      (c.rapports || [])
        .filter((r) => r.empId === e.id)
        .map((r) => ({ ...r, chantier: c.nom || tr('Chantier') })),
    )
    .sort((a, b) => jourTri(b.date).localeCompare(jourTri(a.date)));
  const arrets = absences.filter((c) => (c.type || 'vacances') !== 'vacances');
  const dossier = (e.documents || []).filter((p) => p.fichier || p.statut === 'reçu');
  const initiales = nom
    .split(/\s+/)
    .map((m) => m[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  // Les lettres et certificats émis ne viennent qu'à l'ouverture du casier :
  // les charger pour toute l'équipe à chaque visite du classeur serait lent.
  const ouvre = (ev) => {
    if (!ev.currentTarget.open || lettres !== null) return;
    setLettres([]);
    Promise.resolve()
      .then(() => employesApi.documents(e.id))
      .then((l) => setLettres(Array.isArray(l) ? l : []))
      .catch(() => setLettres([]));
  };

  const etat = (c) => (
    <span style={{ color: c.statut === 'Approuvé' ? C.green : C.amber, fontWeight: 700 }}>
      {tr(c.statut || '')}
    </span>
  );

  return (
    <Card style={{ marginBottom: 6, overflow: 'hidden' }}>
      <details data-testid="casier" onToggle={ouvre}>
        <summary
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 11,
            padding: '12px 14px',
            cursor: 'pointer',
            listStyle: 'none',
          }}
        >
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 12,
              background: C.purple + '18',
              color: C.purple,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 13,
              fontWeight: 900,
              flexShrink: 0,
            }}
          >
            {initiales || '👷'}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12.5, fontWeight: 800 }}>
              {nom}
              {estPatron ? ' · ' + tr('moi') : ''}
              {e.parti || e.dateDepart ? ' · ' + tr('parti') : ''}
            </div>
            <div style={{ fontSize: 9, color: C.muted }}>
              {[
                e.role,
                trf('{0} fiche(s) de salaire', fiches.length),
                trf('{0} absence(s)', absences.length),
              ]
                .filter(Boolean)
                .join(' · ')}
            </div>
          </div>
          <div style={{ fontSize: 10, fontWeight: 800, color: C.purple }}>▸</div>
        </summary>
        <div style={{ padding: '0 14px 6px' }}>
          <Tiroir icone="📄" libelle={tr('Contrat de travail')} resume={tr('1 document')}>
            <div style={ligne}>
              <span>{tr('Contrat')}</span>
              <BoutonDocument
                chemin={`/employes/${e.id}/contrat/document`}
                label={tr('📄 Ouvrir')}
                color={C.purple}
                sm
              />
            </div>
          </Tiroir>

          <Tiroir
            icone="💰"
            libelle={trf('Fiches de salaire {0}', annee)}
            resume={fiches.length ? trf('{0} fiche(s)', fiches.length) : ''}
          >
            {fiches.length === 0 && <div style={vide}>{tr('Aucune fiche pour cette année.')}</div>}
            {/* Un bouton par mois, en grille : douze lignes prenaient l'écran. */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                gap: 6,
                marginTop: 4,
              }}
            >
              {[...fiches].reverse().map((m) => (
                <BoutonDocument
                  key={m}
                  chemin={`/employes/${e.id}/fiche-de-paie/document`}
                  params={{ mois: m }}
                  label={'📄 ' + tr(MOIS[+m.slice(5, 7) - 1])}
                  color={C.purple}
                  full
                  sm
                />
              ))}
            </div>
          </Tiroir>

          <Tiroir icone="🎓" libelle={tr('Certificat de salaire')} resume={String(annee)}>
            <div style={ligne}>
              <span>{trf('Année {0}', annee)}</span>
              <BoutonDocument
                chemin={`/employes/${e.id}/certificat-de-salaire/document`}
                params={{ annee }}
                label={tr('📄 Ouvrir')}
                color={C.purple}
                sm
              />
            </div>
          </Tiroir>

          <Tiroir
            icone="⏱️"
            libelle={trf('Heures {0}', annee)}
            resume={totalHeures ? totalHeures.toFixed(1) + ' h' : ''}
          >
            {moisHeures.length === 0 ? (
              <div style={vide}>{tr('Aucune heure cette année.')}</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={entete}>{tr('Mois')}</th>
                    <th style={entete}>{tr('Chantier')}</th>
                    <th style={{ ...entete, textAlign: 'right' }}>{tr('Heures')}</th>
                  </tr>
                </thead>
                <tbody>
                  {moisHeures.flatMap((m) =>
                    Object.entries(heuresParMois[m].chantiers).map(([ch, h], i) => (
                      <tr key={m + ch} style={{ borderTop: i ? 'none' : '1px solid #f0f2f5' }}>
                        <td style={{ ...cellule, fontWeight: 800 }}>
                          {i ? '' : tr(MOIS[+m.slice(5, 7) - 1])}
                        </td>
                        <td style={{ ...cellule, color: C.grey }}>{ch}</td>
                        <td style={{ ...cellule, textAlign: 'right', fontWeight: 700 }}>
                          {h.toFixed(1)} h
                        </td>
                      </tr>
                    )),
                  )}
                  <tr style={{ borderTop: '2px solid ' + C.border }}>
                    <td style={{ ...cellule, fontWeight: 900 }} colSpan={2}>
                      {trf('Total {0}', annee)}
                    </td>
                    <td style={{ ...cellule, textAlign: 'right', fontWeight: 900 }}>
                      {totalHeures.toFixed(1)} h
                    </td>
                  </tr>
                </tbody>
              </table>
            )}
          </Tiroir>

          <Tiroir
            icone="🛠️"
            libelle={tr('Travaux sur les chantiers')}
            resume={travaux.length ? trf('{0} jour(s)', travaux.length) : ''}
          >
            {travaux.length === 0 && <div style={vide}>{tr('Aucun rapport.')}</div>}
            {travaux.map((r) => (
              <div key={r.id} style={{ ...ligne, display: 'block' }}>
                <div style={{ fontWeight: 800, color: C.ink }}>
                  {r.date} · {r.chantier}
                  {r.heures ? ' · ' + (+r.heures).toFixed(1) + ' h' : ''}
                </div>
                <div style={{ whiteSpace: 'pre-wrap', color: C.grey, marginTop: 2 }}>
                  {r.contenu}
                </div>
              </div>
            ))}
          </Tiroir>

          <Tiroir
            icone="🌴"
            libelle={tr('Vacances')}
            resume={vacances.length ? trf('{0} j', totalJours(vacances)) : ''}
          >
            {vacances.length === 0 ? (
              <div style={vide}>{tr('Aucune demande de vacances.')}</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={entete}>{tr('Dates')}</th>
                    <th style={entete}>{tr('Jours')}</th>
                    <th style={entete}>{tr('État')}</th>
                  </tr>
                </thead>
                <tbody>
                  {vacances.map((c) => (
                    <tr key={c.id} style={{ borderTop: '1px solid #f0f2f5' }}>
                      <td style={cellule}>{periode(c)}</td>
                      <td style={cellule}>{c.jours || '—'}</td>
                      <td style={cellule}>{etat(c)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Tiroir>

          <Tiroir
            icone="🤒"
            libelle={tr('Maladie et accident')}
            resume={
              arrets.length ? trf('{0} arrêt(s) · {1} j', arrets.length, totalJours(arrets)) : ''
            }
          >
            {arrets.length === 0 ? (
              <div style={vide}>{tr('Aucun arrêt.')}</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={entete}>{tr('Type')}</th>
                    <th style={entete}>{tr('Dates')}</th>
                    <th style={entete}>{tr('Jours')}</th>
                    <th style={entete}>{tr('Certificat')}</th>
                  </tr>
                </thead>
                <tbody>
                  {arrets.map((c) => (
                    <tr key={c.id} style={{ borderTop: '1px solid #f0f2f5' }}>
                      <td style={{ ...cellule, fontWeight: 800 }}>
                        {tr(ABSENCES[c.type] || '📅 ' + (c.type || 'Absence'))}
                      </td>
                      <td style={cellule}>{periode(c)}</td>
                      <td style={cellule}>{c.jours || '—'}</td>
                      <td style={cellule}>
                        {c.certificat && typeof c.id === 'number' ? (
                          <Voir
                            adresse={congesApi.adresseCertificat(c.id)}
                            titre={
                              tr(ABSENCES[c.type] || 'Absence') + ' · ' + nom + ' · ' + periode(c)
                            }
                            libelle={tr('📎 Voir')}
                          />
                        ) : (
                          <span style={{ color: C.muted }}>{tr('pas reçu')}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Tiroir>

          <Tiroir
            icone="✉️"
            libelle={tr('Lettres et certificats')}
            resume={lettres && lettres.length ? String(lettres.length) : ''}
          >
            {lettres === null || lettres.length === 0 ? (
              <div style={vide}>
                {lettres === null ? tr('Ouverture…') : tr('Aucune lettre ni certificat émis.')}
              </div>
            ) : (
              lettres.map((d) => (
                <div key={d.id} style={ligne}>
                  <span>
                    {d.titre}
                    <span style={{ color: C.muted, marginLeft: 6 }}>{dateSuisse(d.emisLe)}</span>
                  </span>
                  <BoutonDocument
                    chemin={`/employes/${e.id}/documents/${d.id}`}
                    label={tr('📄 Ouvrir')}
                    color={C.purple}
                    sm
                  />
                </div>
              ))
            )}
          </Tiroir>

          <Tiroir
            icone="🗂"
            libelle={tr('Pièces du dossier')}
            resume={dossier.length ? String(dossier.length) : ''}
          >
            {dossier.length === 0 && <div style={vide}>{tr('Aucune pièce.')}</div>}
            {dossier.map((p) => (
              <div key={p.id} style={ligne}>
                <span>
                  {tr(p.type || p.nom || 'Pièce')}
                  {p.exp ? (
                    <span style={{ color: C.muted, marginLeft: 6 }}>
                      {trf('expire le {0}', dateSuisse(p.exp))}
                    </span>
                  ) : null}
                </span>
                {p.fichier && typeof p.id === 'number' && (
                  <Voir
                    adresse={dossierApi.adresse(e.id, p.id)}
                    titre={tr(p.type || p.nom || 'Pièce') + ' · ' + nom}
                    libelle={tr('📎 Voir')}
                  />
                )}
              </div>
            ))}
          </Tiroir>
        </div>
      </details>
    </Card>
  );
}

/**
 * Un casier par personne dans le classeur : contrat, fiches de salaire,
 * certificat, vacances, maladie et accident, lettres, pièces du dossier.
 *
 * <p>Le classeur rangeait le personnel par type — une tuile des fiches de
 * salaire, une des contrats, une des certificats — et ne montrait ni les
 * vacances ni les arrêts. Le patron voulait, pour chacun, « son casier » :
 * tout ce qui est à lui, au même endroit. Le sien d'abord.
 *
 * @param {object} props propriétés
 * @param {object[]} props.employes fiches du personnel
 * @param {object[]} [props.conges] demandes de vacances et arrêts
 * @param {object[]} [props.chantiers] chantiers, pour les heures et les rapports de chacun
 * @param {number} props.annee exercice affiché
 * @param {(e: object) => boolean} [props.estLaMienne] vrai pour la fiche du patron connecté
 * @param {object} props.styleGroupe style du titre de groupe du classeur
 * @returns {import('react').ReactElement} les casiers
 */
export function CasiersPersonnel({ employes, conges, chantiers, annee, estLaMienne, styleGroupe }) {
  const moi = (e) => !!(estLaMienne && estLaMienne(e));
  const parti = (e) => !!(e.parti || e.dateDepart);
  const liste = (employes || [])
    .filter((e) => Number.isInteger(e.id))
    .sort(
      (a, b) =>
        Number(moi(b)) - Number(moi(a)) ||
        Number(parti(a)) - Number(parti(b)) ||
        (nomComplet(a) || '').localeCompare(nomComplet(b) || ''),
    );
  return (
    <>
      <div style={styleGroupe}>{tr('👷 Personnel — un casier par personne')}</div>
      {liste.length === 0 && (
        <Card>
          <div style={{ padding: 14, textAlign: 'center', color: C.muted, fontSize: 11 }}>
            {tr('Aucun employé pour l’instant.')}
          </div>
        </Card>
      )}
      {liste.map((e) => (
        <Casier
          key={e.id}
          e={e}
          chantiers={chantiers}
          annee={annee}
          estPatron={moi(e)}
          absences={(conges || [])
            .filter((c) => c.empId === e.id)
            .sort((a, b) => jourTri(b.debut).localeCompare(jourTri(a.debut)))}
        />
      ))}
    </>
  );
}
